import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import type { BudgetData } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor";
import { validateScheduledBudgetForm } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/validateScheduledBudgetForm";
import * as y from "yup";

export { flattenScheduledBudgetFormErrors as flattenEditBudgetModalErrors } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/scheduledBudgetRhfErrors";

/** Passed through `useForm({ context })` for scheduled timeline + delivery-window checks (same pattern as {@link CreateCampaignFlow}). */
export type EditBudgetModalValidationContext = {
  eventTimezone?: string;
  /** Full ad delivery window (campaign run start/end, UTC ISO). */
  scheduledBudgetDeliveryWindow?: {
    deliveryWindowStartIso: string;
    deliveryWindowEndIso: string;
  } | null;
};

/**
 * Yup schema for Ads Engage “Edit budget” / {@link GuidedBudgetEditor} values — mirrors
 * {@link campaignFlowForm} scheduled rules (timeline, positive phase amounts, ad delivery window)
 * without campaign `schedule` (delivery-window bounds come from context).
 */
export const EditBudgetModalSchema = y
  .object({
    type: y.string().optional(),
    amount: y
      .object({
        amount: y.mixed().nullable().optional(),
        currency: y.string().optional(),
      })
      .optional(),
    base: y.mixed().optional(),
    segments: y
      .array()
      .of(
        y.object({
          time_start: y.string().optional(),
          time_end: y.string().optional(),
          amount: y
            .object({
              amount: y.mixed().nullable().optional(),
              currency: y.string().optional(),
            })
            .optional(),
        })
      )
      .default([]),
  })
  .test(
    "edit-scheduled-budget",
    "Invalid scheduled budget",
    function (value) {
      if (!value || value.type !== "scheduled") return true;
      const ctx = this.options.context as
        | EditBudgetModalValidationContext
        | undefined;
      const segments = (value as BudgetData).segments ?? [];
      const tz =
        (ctx?.eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;

      const deliveryWindow = ctx?.scheduledBudgetDeliveryWindow;
      const fieldError = validateScheduledBudgetForm({
        segments,
        eventTimezone: tz,
        deliveryWindow: deliveryWindow
          ? {
              deliveryWindowStartIso: deliveryWindow.deliveryWindowStartIso,
              deliveryWindowEndIso: deliveryWindow.deliveryWindowEndIso,
            }
          : null,
      });
      if (fieldError) {
        return this.createError({
          path: fieldError.pathSuffix ?? "segments",
          message: fieldError.message,
        });
      }
      return true;
    }
  );
